import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin, Truck, ShieldCheck, Phone, ChevronRight } from 'lucide-react';
import locations from '../data/locations';
import catalogueStructure from '../data/catalogueStructure';
import JsonLd from '../components/JsonLd';
import { SITE_URL } from '../data/seoConfig';

const DEFAULT_CITY = {
  name: 'Normandie',
  title: 'Location de barnum et chapiteau en Normandie',
  intro:
    "Barnum Événement, basé au Havre, assure la location, la livraison et l'installation de barnums, chapiteaux et matériel événementiel dans toute la Normandie pour vos mariages, réceptions et événements professionnels.",
  paragraphs: [
    "Notre parc de matériel professionnel couvre l'ensemble de vos besoins : structures couvertes, mobilier, vaisselle, accessoires techniques et packs tout inclus. Chaque location s'accompagne de conseils personnalisés pour choisir les dimensions et les équipements adaptés à votre événement.",
    "Nous intervenons sur l'ensemble du territoire normand avec un service de proximité réactif.",
  ],
  nearby: ['Le Havre', 'Rouen', 'Fécamp', 'Étretat', 'Honfleur'],
};

const CityParagraph = ({ text }) => (
  <p className="text-base text-gray-600 leading-relaxed mb-5">{text}</p>
);

const CategoryLink = ({ slug, emoji, name }) => (
  <Link
    to={'/catalogue/' + slug}
    className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-orange-50 hover:text-orange-700 transition-colors"
    data-testid={'location-cat-' + slug}
  >
    <span className="text-2xl">{emoji}</span>
    <span className="font-medium text-sm">{name}</span>
  </Link>
);

const LocationPage = () => {
  const { slug } = useParams();
  const city = locations[slug] || DEFAULT_CITY;
  const paragraphs = city.paragraphs || [];

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Location de barnum, chapiteau et matériel événementiel',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Barnum Événement',
      telephone: '+33623893298',
      url: SITE_URL,
    },
    areaServed: { '@type': 'City', name: city.name },
    name: city.title,
    description: city.intro,
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24" data-testid="location-page">
      <JsonLd data={serviceJsonLd} />

      {/* Hero */}
      <section className="bg-gradient-to-r from-orange-600 to-orange-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 text-orange-100 mb-3">
            <MapPin className="w-5 h-5" />
            <span className="text-sm font-medium uppercase tracking-wide">{city.name} · Normandie</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="location-h1">
            {city.title}
          </h1>
          <p className="text-lg md:text-xl text-orange-100 max-w-3xl mx-auto">{city.intro}</p>
          <Link
            to="/devis"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-white text-orange-600 font-semibold rounded-full hover:bg-orange-50 transition-colors"
            data-testid="location-cta-devis"
          >
            Demander un devis gratuit <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Contenu */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            Votre spécialiste de la location événementielle à {city.name}
          </h2>
          {paragraphs.map((p, i) => (
            <CityParagraph key={i} text={p} />
          ))}
        </div>
      </section>

      {/* Atouts */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-3 gap-8">
          <div className="text-center">
            <Truck className="w-10 h-10 text-orange-500 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-1">Livraison & installation</h3>
            <p className="text-sm text-gray-600">Montage et démontage assurés par notre équipe à {city.name}.</p>
          </div>
          <div className="text-center">
            <ShieldCheck className="w-10 h-10 text-orange-500 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-1">Matériel professionnel</h3>
            <p className="text-sm text-gray-600">Structures robustes, étanches et contrôlées régulièrement.</p>
          </div>
          <div className="text-center">
            <Phone className="w-10 h-10 text-orange-500 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-1">Conseil personnalisé</h3>
            <p className="text-sm text-gray-600">Un devis gratuit et des conseils adaptés à votre événement.</p>
          </div>
        </div>
      </section>

      {/* Catégories */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
            Notre matériel en location à {city.name}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {catalogueStructure.map((cat) => (
              <CategoryLink key={cat.slug} slug={cat.slug} emoji={cat.emoji} name={cat.name} />
            ))}
          </div>
        </div>
      </section>

      {/* Zones desservies */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Zones desservies autour de {city.name}</h2>
          <p className="text-gray-600 mb-2">
            Nous livrons et installons également à :{' '}
            <span className="font-medium text-gray-800">{city.nearby.join(', ')}</span>.
          </p>
          <p className="text-gray-600">
            Vous ne trouvez pas votre commune ?{' '}
            <Link to="/devis" className="text-orange-600 font-medium underline">
              Contactez-nous
            </Link>{' '}
            pour vérifier notre disponibilité.
          </p>
        </div>
      </section>

      {/* CTA final */}
      <section className="py-16 bg-gradient-to-r from-orange-600 to-orange-700">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Organisez votre événement à {city.name} en toute sérénité
          </h2>
          <p className="text-orange-100 mb-6">
            Demandez votre devis gratuit et personnalisé : réponse rapide par notre équipe locale.
          </p>
          <Link
            to="/devis"
            className="inline-flex items-center gap-2 px-8 py-3 bg-white text-orange-600 font-semibold rounded-full hover:bg-orange-50 transition-colors"
          >
            Obtenir mon devis gratuit <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LocationPage;
